import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import MediaElement from './MediaElement';
import Link from './Link';
import { u } from '../common/Units';

interface ImageMapPopupProps {
  documentId: number,
  contentType: string,
  title: string,
  subtitle?: string,
  image?: any,
  position: { x: number, y: number },
  markerColor?: string,
  onClick?: () => void,
  /** When true, popup is shown immediately (used by map selection) */
  defaultVisible?: boolean,
  /** Locked placement — skips auto flip based on position */
  placement?: { left?: boolean, bottom?: boolean },
  /** Override default caret offsets (pin head). Circle markers need different values. */
  offset?: { x: number, y: number },
  /** When true, the popup is shown but does not link to the presentation */
  notClickable?: boolean
}

/** Vertical anchor on the marker (pin head / circle center), in marker-local px */
const MARKER_ANCHOR_Y = 20;

const PopupWrapper = styled.div<{ $hasMedia?: boolean, $offsetX: number, $offsetY: number }>`
  position: absolute;
  z-index: 12;
  top: ${({ $offsetY }) => $offsetY}px;
  left: ${({ $offsetX }) => $offsetX}px;
  /* Keep caret locked to the marker head regardless of popup height */
  transform: translate3d(0, -50%, 0);
  opacity: 0;
  pointer-events: none;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.4));

  &::before {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    background: #fff;
    top: 50%;
    left: -6px;
    transform: translateY(-50%) rotate(45deg);
  }

  &.left {
    right: ${({ $offsetX }) => $offsetX}px;
    left: unset;
  }

  &.left::before {
    left: unset;
    right: -6px;
  }

  /*
   * Near viewport bottom: only shift tall (media) popups upward.
   * Text-only popups stay centered — an -85% shift looks awkward on short bubbles.
   */
  &.bottom {
    transform: ${({ $hasMedia }) =>
      $hasMedia ? "translate3d(0, -85%, 0)" : "translate3d(0, -50%, 0)"};
  }

  &.bottom::before {
    top: ${({ $hasMedia }) => ($hasMedia ? "85%" : "50%")};
  }

  &.visible {
    opacity: 1;
    transition: opacity 0.5s;
    pointer-events: all;
  }

  & a,
  & .link {
    display: block;
    text-decoration: none;
    color: inherit;
  }
`;

const PopupBody = styled.div<{ $hasMedia?: boolean }>`
  position: relative;
  background-color: #fff;
  border-radius: 6px;
  overflow: hidden;
  width: ${({ $hasMedia }) => ($hasMedia ? u(250) : "max-content")};
  max-width: ${u(280)};
`;

const StyledMedia = styled(MediaElement)`
  width: 100%;
  height: ${u(150)};
  border-radius: 0;
  background-color: transparent;
`;

const Captions = styled.div<{ $hasMedia?: boolean }>`
  padding: ${({ $hasMedia }) =>
    $hasMedia
      ? `${u(10)} ${u(12)} ${u(12)}`
      : `${u(10)} ${u(14)}`};
  display: flex;
  flex-direction: column;
  gap: ${u(3)};
`;

const Accent = styled.span<{ $accentColor?: string }>`
  display: block;
  width: 22px;
  height: 3px;
  background-color: ${({ $accentColor, theme }) => $accentColor ?? theme.accentColor};
  margin-bottom: 1px;
`;

const Title = styled.span`
  color: #111;
  font-weight: 600;
  font-size: 1em;
  line-height: 1.25;
  display: block;
`;

const Subtitle = styled.span`
  color: #555;
  font-size: 0.88em;
  line-height: 1.3;
  display: block;
  letter-spacing: 0.2px;
`;

/**
 * Render a popup callout for the imagemap view.
 * The caret is vertically locked to the marker head so it stays aligned
 * whether the popup shows media or text only.
 * @returns {JSX.Element} Component template
 */
const ImageMapPopup: FC<ImageMapPopupProps> = ({
  documentId,
  contentType,
  title,
  subtitle,
  image,
  position,
  markerColor,
  onClick,
  defaultVisible,
  placement,
  offset,
  notClickable
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const hasMedia = Boolean(image?.url);
  const hasTitle = title?.length > 1;
  const hasSubtitle = Boolean(subtitle && subtitle.length > 1);
  const offsetX = offset?.x ?? 58;
  const offsetY = offset?.y ?? MARKER_ANCHOR_Y;
  const placementClass = [
    defaultVisible ? "visible" : "",
    placement?.left ? "left" : "",
    placement?.bottom ? "bottom" : ""
  ].filter(Boolean).join(" ");

  // Flip popup side when the marker is near the viewport edge (imagemap only — map passes locked placement)
  useEffect(() => {
    if (!popupRef.current || placement) { return; }

    const arrowPosition: string[] = [];
    if (position?.x > 800) { arrowPosition.push("left"); }
    if (position?.y > 600) { arrowPosition.push("bottom"); }

    popupRef.current.classList.remove("left", "bottom");
    arrowPosition.forEach((pos: string) => {
      popupRef.current?.classList.add(pos);
    });
  }, [position, placement]);

  return (
    <PopupWrapper
      ref={popupRef}
      onClick={onClick}
      id={`imagemappopup-${documentId}`}
      className={`imagemappopup ${placementClass}`.trim()}
      $hasMedia={hasMedia}
      $offsetX={offsetX}
      $offsetY={offsetY}
    >
      <PopupBody $hasMedia={hasMedia}>
        <Link id={documentId} presentationType={contentType} notClickable={Boolean(notClickable)}>
          <>
            {hasMedia && <StyledMedia media={image} backgroundColor="transparent" />}
            {(hasTitle || hasSubtitle) && (
              <Captions $hasMedia={hasMedia}>
                {hasMedia && <Accent $accentColor={markerColor} />}
                {hasTitle && <Title>{title}</Title>}
                {hasSubtitle && <Subtitle>{subtitle}</Subtitle>}
              </Captions>
            )}
          </>
        </Link>
      </PopupBody>
    </PopupWrapper>
  );
}

export default ImageMapPopup;
